import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  // AutoFixHigh as SuggestIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
  Autorenew as AutorenewIcon,
  Save as SaveIcon,
  AccountTree as WorkflowCanvasIcon,
  OpenInFull as OpenInFullIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
} from '@mui/icons-material';
import {
  generateBusinessRules,
  saveChannelBusinessRules,
  validateChannelBusinessRules,
  type BusinessRulesGenerationMode,
  type BusinessRulesValidationResult,
  type BusinessRulesValidationIssue,
} from '../../services/businessRulesService';
import { isImproveRulesEnabled } from '../../config/runtimeConfig';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import DescriptionCards from '../AddSpaceModal/DescriptionCards';
import { modalStyles } from '../../styles/modalStyles';
import { measureScrollbarWidth } from '../../helpers/domUtils';

const RULES_ALREADY_STRUCTURED_MESSAGE = 'Your rules already follow the recommended structure';
const RULES_REFINE_UNAVAILABLE_MESSAGE = 'Improving existing rules is not available right now.';

const RULES_EXPAND_ICON_BASE_RIGHT = 8;
const RULES_EXPAND_ICON_SCROLL_GAP = 8;

// Fix 14/Item 7 — loose match on `check` ONLY, not exact-equality: it's LLM-generated free text
// (business_rule_validator.py's prompt only constrains it to "a short snake_case identifier", not
// a fixed enum), so a substring match is still needed. Matches any of the app types the
// validator's own system prompt already knows about, not just CRM. Worst case on a miss: that
// warning falls into the "everything else" bucket instead and gets folded into the generic
// sentence — never a crash, never an invented app name.
//
// PR #648 review (C1, Critical): the first draft also matched against `message`, which
// misclassifies the real-world case — business_rule_validator.py's prompt requires every warning
// message to end in a fix suggestion, and for missing-email findings that's explicitly "...or
// connect a ticketing app"/"...or connect a CRM." Matching message text against these same hints
// meant every genuinely fixable missing-email warning that happened to mention an app in its own
// fix suggestion got misclassified as an app-connection tip — reintroducing the wall-of-tips
// problem this feature exists to remove, just re-labeled. `check` is a stable, narrow identifier
// (e.g. "crm_not_connected") set once by the validator for what this warning IS, not free text
// about what fixing it might involve — company PR #620 (orchestrator) hit and fixed the identical
// bug the same way.
const APP_CONNECTION_HINTS = [
  'crm', 'calendar', 'ticket', 'jira', 'zendesk', 'servicenow',
  'freshdesk', 'hubspot', 'zoho', 'odoo', 'salesforce', 'app_not_connected',
];

function isAppConnectionWarning(w: string | BusinessRulesValidationIssue): boolean {
  const check = typeof w === 'string' ? '' : w.check || '';
  const haystack = check.toLowerCase();
  return APP_CONNECTION_HINTS.some((hint) => haystack.includes(hint));
}

function partitionWarnings(warnings: (string | BusinessRulesValidationIssue)[]) {
  const appWarnings = warnings.filter(isAppConnectionWarning);
  const otherWarnings = warnings.filter((w) => !isAppConnectionWarning(w));
  return { appWarnings, otherWarnings };
}

interface BusinessRulesSectionProps {
  /** 'display' = read-only (matches Purpose of the channel). 'edit' = suggest + textarea. */
  variant?: 'display' | 'edit';
  /** 'immediate' = Save button calls API. 'deferred' = parent saves via onRulesChange. */
  saveMode?: 'immediate' | 'deferred';
  /**
   * 'auto' = fetch suggestions when description/workspace changes (new channel flow).
   * 'open' = fetch once when section mounts if no saved rules (edit channel dialog).
   */
  suggestTrigger?: 'auto' | 'open';
  channelId?: string;
  workspaceName?: string;
  channelDescription?: string;
  channelTags?: string[];
  /** Subtags selected by admin (e.g. ["payroll", "leave request"]) — forwarded to the backend
   * as `tags` in the generate-business-rules request. */
  channelSubtags?: string[];
  emailId?: string;
  initialBusinessRules?: string;
  /** Controlled value when saveMode is 'deferred'. Must be passed together with onRulesChange. */
  value?: string;
  onRulesChange?: (rules: string) => void;
  isChannelAdmin?: boolean;
  onSaved?: (rules: string) => void;
  /** Allow expand icon/dialog in auto-suggest flow when needed (e.g. configure email modal). */
  enableExpandInAuto?: boolean;
  /** When 'message', suggestions only require description + workspace (no connected email). */
  channelType?: 'email' | 'message';
  /** Raw platform type forwarded to the ML backend (e.g. 'google_chat', 'slack', 'teams', 'regular').
   * Omit for email channels — backend defaults to email when absent. */
  channelSubType?: string;
  externalValidationResult?: BusinessRulesValidationResult | null;
  onValidationMetaChange?: (meta: {
    status: 'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean';
    canUpdate: boolean;
    forceSave: boolean;
    result: BusinessRulesValidationResult | null;
  }) => void;
  /** Called whenever the suggestions-fetching state changes — lets a parent disable Next while loading. */
  onSuggestingChange?: (isSuggesting: boolean) => void;
  /** suggestTrigger="open" only: bump this (parent increments a counter) whenever the admin
   * explicitly picks a new description from suggestions — the only thing allowed to refresh
   * business-rule suggestions in the Edit Channel dialog. Tags/subtags edits alone must NOT
   * trigger a refetch here; the effect below only reacts to a change in this value, skipping
   * the initial mount. */
  descriptionSelectionNonce?: number;
  /** When true, shows a red asterisk on the "Business Rules" label (e.g. the email setup wizard,
   * where leaving this blank must not be allowed to advance). Defaults to false everywhere else
   * so the channel-edit dialog and message/voice setup flows are unaffected. */
  required?: boolean;
  /** Textarea character limit. Defaults to 8000 (existing behavior everywhere) — pass a smaller
   * value (e.g. 4000) where the backend enforces a tighter max_length. */
  maxLength?: number;
  /** When true, appends a "current/max" character count to the helper text. Defaults to false
   * so every existing consumer's helper text is unchanged. */
  showCharacterCount?: boolean;
  /** Business_rule_canvas branch (PRISM7_CHANNEL_RULES_CANVAS_TDD.md §8.2) — when provided, shows
   * a "See workflow canvas" button next to Save, using the exact same enabled/disabled gate as
   * Save (rules non-empty AND validationStatus is 'clean' or 'warnings'). Called with the current
   * rules text on click; the parent's own implementation is expected to just call its existing
   * navigateToTab('workflow') helper (TDD §8.2) — the text argument is passed through in case a
   * caller needs it, but is not required for that navigation itself. Omit to keep this section
   * exactly as it renders today (e.g. the message-channel edit dialog, which doesn't pass this
   * prop yet). */
  onBuildWorkflow?: (rulesText: string) => void;
}

const BusinessRulesSection: React.FC<BusinessRulesSectionProps> = ({
  variant = 'edit',
  saveMode = 'immediate',
  suggestTrigger = 'auto',
  channelId = '',
  workspaceName = '',
  channelDescription = '',
  channelTags = [],
  channelSubtags = [],
  emailId = '',
  initialBusinessRules = '',
  value,
  onRulesChange,
  isChannelAdmin = true,
  onSaved,
  enableExpandInAuto = false,
  channelType = 'email',
  channelSubType,
  externalValidationResult = null,
  onValidationMetaChange,
  onSuggestingChange,
  required = false,
  descriptionSelectionNonce,
  maxLength = 8000,
  showCharacterCount = false,
  onBuildWorkflow,
}) => {
  const { showSuccess, showError } = useToast();
  const { isDarkMode } = useTheme();

  const isControlled = value !== undefined && onRulesChange !== undefined;
  const [internalRules, setInternalRules] = useState<string>(initialBusinessRules);
  const editedRules = isControlled ? value : internalRules;

  const setEditedRules = (rules: string) => {
    if (isControlled) {
      onRulesChange(rules);
    } else {
      setInternalRules(rules);
    }
  };

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showRulesCards, setShowRulesCards] = useState(false);
  const [showRulesField, setShowRulesField] = useState(true);
  const [isSuggesting, setIsSuggesting] = useState(false);
  useEffect(() => { onSuggestingChange?.(isSuggesting); }, [isSuggesting, onSuggestingChange]);
  // "Improve my rules" (TDD §6.4): the cards on screen are a refinement of the saved rules,
  // as opposed to fresh suggestions. Drives bold-new-sentences and confirm-before-replace.
  const [refineActive, setRefineActive] = useState(false);
  const [refineNotice, setRefineNotice] = useState<string | null>(null);
  // Set when a refine response comes back without `mode` (backend flag off / older backend):
  // the button is hidden for the rest of this mount so the admin is not offered a dead action.
  const [refineUnsupported, setRefineUnsupported] = useState(false);
  const [pendingReplacement, setPendingReplacement] = useState<string | null>(null);
  // Mode the backend echoed for the suggestion cards on screen. Only present when its context
  // flag (and so its address-free rules) is on — the caption under the cards depends on it.
  const [suggestionsMode, setSuggestionsMode] = useState<BusinessRulesGenerationMode | undefined>(undefined);
  const hasGeneratedOnceRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isRulesExpanded, setIsRulesExpanded] = useState(false);
  const [validationStatus, setValidationStatus] = useState<
    'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean'
  >('idle');
  const [validationResult, setValidationResult] = useState<BusinessRulesValidationResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [rulesFieldFocused, setRulesFieldFocused] = useState(false);
  const validationRequestIdRef = useRef(0);
  const hasAutoValidatedOnMountRef = useRef(false);
  const [rulesHasVerticalScroll, setRulesHasVerticalScroll] = useState(false);
  const [rulesScrollbarWidth, setRulesScrollbarWidth] = useState(0);
  const rulesTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const workflowActionButtonsRef = useRef<HTMLDivElement | null>(null);

  const resetRulesScrollState = useCallback(() => {
    setRulesHasVerticalScroll(false);
    setRulesScrollbarWidth(0);
  }, []);

  const updateRulesScrollState = useCallback(() => {
    const el = rulesTextareaRef.current;
    if (!el) return;
    const hasScroll = el.scrollHeight > el.clientHeight + 1;
    const scrollbarWidth = measureScrollbarWidth(el, hasScroll);
    setRulesHasVerticalScroll(hasScroll);
    setRulesScrollbarWidth(scrollbarWidth);
  }, []);

  const hasDescription = Boolean(channelDescription?.trim());
  const hasWorkspace = Boolean(workspaceName?.trim());
  const hasEmail = Boolean(emailId?.trim());
  const isMessagingChannel = channelType === 'message';
  const canSuggest = hasDescription && hasWorkspace && (isMessagingChannel || hasEmail);
  const displayRules = initialBusinessRules?.trim() || editedRules?.trim() || '';

  useEffect(() => {
    if (!isControlled) {
      setInternalRules(initialBusinessRules);
    }
  }, [initialBusinessRules, isControlled]);

  useEffect(() => {
    setValidationStatus('idle');
    setValidationResult(null);
    setValidationError(null);
    hasAutoValidatedOnMountRef.current = false;
  }, [initialBusinessRules]);

  useEffect(() => {
    if (!externalValidationResult) return;
    validationRequestIdRef.current += 1;
    setValidationResult(externalValidationResult);
    setValidationError(null);
    setValidationStatus(externalValidationResult.status);
  }, [externalValidationResult]);

  useEffect(() => {
    const hasRules = Boolean(editedRules?.trim());
    const canUpdate =
      hasRules && (validationStatus === 'clean' || validationStatus === 'warnings');
    onValidationMetaChange?.({
      status: validationStatus,
      canUpdate,
      forceSave: validationStatus === 'warnings',
      result: validationResult,
    });
  }, [editedRules, onValidationMetaChange, validationResult, validationStatus]);

  // Business_rule_canvas branch — the Edit Channel dialog's rules textarea can be tall enough
  // that the "See workflow canvas" button sits below the fold once validation finishes and the
  // button enables; the admin had to already know to scroll down to find it. Auto-scroll it into
  // view the moment validation completes (only relevant when onBuildWorkflow is actually
  // rendered — this same component's plain "immediate" save-only usages elsewhere are unaffected).
  useEffect(() => {
    if (!onBuildWorkflow) return;
    if (validationStatus !== 'clean' && validationStatus !== 'warnings') return;
    // Optional chaining on scrollIntoView itself, not just .current — jsdom (this codebase's
    // test environment) doesn't implement it at all, so this must stay a safe no-op there
    // rather than throwing "scrollIntoView is not a function".
    workflowActionButtonsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [validationStatus, onBuildWorkflow]);

  const categoriesKey = useMemo(() => JSON.stringify(channelTags), [channelTags]);
  const subtagsKey = useMemo(() => JSON.stringify(channelSubtags), [channelSubtags]);
  const suggestRequestIdRef = useRef(0);
  const lastSuggestKeyRef = useRef<string>('');
  const hasFetchedOnOpenRef = useRef(false);

  const serverRules = initialBusinessRules?.trim() ?? '';
  const hasExistingRules = Boolean(serverRules);
  const [showSavedRulesView, setShowSavedRulesView] = useState(
    () => suggestTrigger === 'open' && Boolean(initialBusinessRules?.trim()),
  );

  const buildSuggestKey = (trimmedDescription: string) =>
    JSON.stringify({
      emailId,
      workspaceName,
      categories: channelTags,
      tags: channelSubtags,
      description: trimmedDescription,
      channelSubType,
    });

  // refine = the admin clicked "Improve my rules": an explicit action, so it bypasses the
  // duplicate-request guard and never touches the textarea or its validation state.
  const fetchSuggestions = async (
    trimmedDescription: string,
    options?: { clearRules?: boolean; trackOpenFetch?: boolean; refine?: boolean },
  ) => {
    const isRefine = options?.refine === true;
    const suggestKey = buildSuggestKey(trimmedDescription);

    if (!isRefine && lastSuggestKeyRef.current === suggestKey) return;

    const requestId = ++suggestRequestIdRef.current;

    // channel_id lets the backend ground the rules in the channel's connected apps / saved rules.
    // The server picks the mode itself: a channel with saved rules becomes "refine" no matter what
    // the client asks. So it is sent only when that is what we want:
    //  - "Improve my rules" (refine): always.
    //  - Plain suggestions (both flows): only while the channel has NO saved rules
    //    (create+context). With saved rules, a description-change refresh must stay a normal
    //    "new suggestions from the new description" request, not turn into a refine.
    //  - New-channel flow ('auto'): additionally never on the very first generation (the channel
    //    does not exist yet, so there is nothing to ground in) — only on later regenerations once
    //    an id exists.
    const sendChannelId =
      Boolean(channelId.trim()) &&
      (isRefine ||
        (!hasExistingRules && (suggestTrigger === 'open' || hasGeneratedOnceRef.current)));
    hasGeneratedOnceRef.current = true;

    setIsSuggesting(true);
    setSuggestError(null);
    setRefineNotice(null);
    setShowRulesCards(true);
    setShowRulesField(false);
    if (isRefine) {
      // The saved-rules view returns early and would hide the cards/loading state.
      setShowSavedRulesView(false);
    } else {
      setRefineActive(false);
    }
    if (options?.clearRules !== false) {
      setEditedRules('');
    }

    // Back to the saved-rules view with a message; nothing to choose from.
    const restoreSavedView = (notice: string) => {
      setRefineNotice(notice);
      setSuggestions([]);
      setShowRulesCards(false);
      setShowRulesField(true);
      setShowSavedRulesView(true);
    };

    try {
      const result = await generateBusinessRules(
        emailId,
        workspaceName,
        channelTags,
        trimmedDescription,
        channelSubtags,
        channelSubType,
        { channelId: sendChannelId ? channelId : undefined },
      );

      if (requestId !== suggestRequestIdRef.current) return;

      if (isRefine) {
        if (!result.mode) {
          // Backend did not echo a mode: refine is off (flag) or unsupported. Whatever came back
          // is plain generation, not an improvement of the saved rules — discard it.
          setRefineUnsupported(true);
          restoreSavedView(RULES_REFINE_UNAVAILABLE_MESSAGE);
          return;
        }
        if (result.rules.length === 0) {
          restoreSavedView(RULES_ALREADY_STRUCTURED_MESSAGE);
          return;
        }
        setRefineActive(true);
        setSuggestions(result.rules);
        return;
      }

      // Sent a channel_id but got no `mode` back: the backend's context/refine switch is off, so
      // "Improve my rules" cannot work — hide it (rollback path; legacy UI stays as today).
      if (sendChannelId && !result.mode) {
        setRefineUnsupported(true);
      }

      if (result.rules.length === 0) {
        setSuggestError('No automation rule suggestions were returned. Try again or write your own.');
        setSuggestions([]);
        setShowRulesCards(false);
        setShowRulesField(true);
        if (options?.trackOpenFetch) {
          hasFetchedOnOpenRef.current = false;
        }
        return;
      }

      setSuggestions(result.rules);
      setSuggestionsMode(result.mode);
      lastSuggestKeyRef.current = suggestKey;
      if (result.validation_result) {
        setValidationResult(result.validation_result);
        setValidationStatus(result.validation_result.status);
      } else {
        setValidationResult(null);
        setValidationStatus(result.rules.length > 0 ? 'pending' : 'idle');
      }
      setValidationError(null);
      if (options?.trackOpenFetch) {
        hasFetchedOnOpenRef.current = true;
      }
    } catch (err: unknown) {
      if (requestId !== suggestRequestIdRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to generate suggestions. Try again.';
      setSuggestError(message);
      setSuggestions([]);
      setShowRulesCards(false);
      setShowRulesField(true);
      if (isRefine) {
        setShowSavedRulesView(true);
      }
      if (options?.trackOpenFetch) {
        hasFetchedOnOpenRef.current = false;
      }
    } finally {
      if (requestId === suggestRequestIdRef.current) {
        setIsSuggesting(false);
      }
    }
  };

  // New channel: auto-fetch when description/workspace/email are ready
  useEffect(() => {
    if (variant === 'display' || suggestTrigger !== 'auto') return;

    const trimmedDescription = channelDescription?.trim() || '';

    if (!canSuggest) {
      if (!trimmedDescription) {
        setSuggestions([]);
        setShowRulesCards(false);
        setShowRulesField(true);
        setSuggestError(null);
        setIsSuggesting(false);
        lastSuggestKeyRef.current = '';
      }
      return;
    }

    fetchSuggestions(trimmedDescription);
  }, [
    variant,
    suggestTrigger,
    canSuggest,
    channelDescription,
    workspaceName,
    emailId,
    categoriesKey,
    subtagsKey,
    channelSubType,
  ]);

  // Edit channel: if saved rules arrive after mount, prefer them over in-flight suggestions
  useEffect(() => {
    if (suggestTrigger !== 'open' || !serverRules) return;

    suggestRequestIdRef.current += 1;
    setIsSuggesting(false);
    setSuggestions([]);
    setShowRulesCards(false);
    setShowRulesField(true);
    setShowSavedRulesView(true);
    hasFetchedOnOpenRef.current = true;

    if (isControlled && !value?.trim()) {
      onRulesChange?.(serverRules);
    }
  }, [serverRules, suggestTrigger, isControlled, value, onRulesChange]);

  // Edit channel dialog: the saved-rules view (above) never re-fetches suggestions on its
  // own. Per product decision, tags/subtags edits alone must NOT trigger a business-rules
  // refresh here — only the admin explicitly picking a new description from suggestions
  // does (descriptionSelectionNonce, bumped by the parent's onSelect/onEdit handlers).
  // Track the nonce and, on an actual change after the initial render, leave the
  // saved-rules view and refresh suggestions using the new description + current tags.
  // clearRules: false preserves whatever the admin has already typed/selected — this only
  // refreshes the suggestion cards, it never overwrites the rules text on its own.
  const descriptionSelectionNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (suggestTrigger !== 'open' || descriptionSelectionNonce === undefined) return;

    if (descriptionSelectionNonceRef.current === null) {
      descriptionSelectionNonceRef.current = descriptionSelectionNonce;
      return;
    }
    if (descriptionSelectionNonceRef.current === descriptionSelectionNonce) return;
    descriptionSelectionNonceRef.current = descriptionSelectionNonce;

    const trimmedDescription = channelDescription?.trim() || '';
    if (!canSuggest || !trimmedDescription) return;

    setShowSavedRulesView(false);
    fetchSuggestions(trimmedDescription, { clearRules: false });
  }, [suggestTrigger, descriptionSelectionNonce, canSuggest, channelDescription]);

  // Edit channel dialog: fetch once on open when no saved rules exist
  useEffect(() => {
    if (variant === 'display' || suggestTrigger !== 'open') return;
    if (hasExistingRules || showSavedRulesView) return;
    if (hasFetchedOnOpenRef.current) return;

    const trimmedDescription = channelDescription?.trim() || '';
    if (!canSuggest || !trimmedDescription) return;

    fetchSuggestions(trimmedDescription, { clearRules: false, trackOpenFetch: true });
  }, [suggestTrigger, canSuggest, channelDescription, hasExistingRules, showSavedRulesView, variant]);

  // PR #672 review (minor) — if the "View suggestions" peek's fetch fails or returns nothing,
  // return to the saved-rules view (with its own "View suggestions" to retry) instead of
  // stranding the admin in the non-saved view with no cards, no loader, and no button to get
  // back. Scoped to hasExistingRules so the create-channel flow (no saved view to return to)
  // is untouched — that flow never sets showSavedRulesView true in the first place.
  useEffect(() => {
    if (suggestTrigger !== 'open' || !hasExistingRules) return;
    if (showSavedRulesView || isSuggesting) return;
    if (suggestions.length > 0 || !suggestError) return;

    setShowSavedRulesView(true);
  }, [suggestTrigger, hasExistingRules, showSavedRulesView, isSuggesting, suggestions.length, suggestError]);

  const showRulesExpand = suggestTrigger === 'open' || (suggestTrigger === 'auto' && enableExpandInAuto);

  useEffect(() => {
    if (variant === 'display' || !showRulesExpand) {
      resetRulesScrollState();
      return;
    }

    const frame = requestAnimationFrame(updateRulesScrollState);
    return () => cancelAnimationFrame(frame);
  }, [editedRules, showRulesExpand, updateRulesScrollState, resetRulesScrollState, variant, showRulesField, showSavedRulesView]);

  useEffect(() => {
    if (variant === 'display' || !showRulesExpand) {
      resetRulesScrollState();
      return;
    }

    let observer: ResizeObserver | null = null;
    const frame = requestAnimationFrame(() => {
      const el = rulesTextareaRef.current;
      if (!el) return;

      updateRulesScrollState();
      observer = new ResizeObserver(updateRulesScrollState);
      observer.observe(el);
    });

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [showRulesExpand, updateRulesScrollState, resetRulesScrollState, showSavedRulesView, showRulesField, variant]);

  const handleRulesInputChange = (nextValue: string) => {
    setEditedRules(nextValue);
    setSaveSuccess(false);
    setValidationResult(null);
    setValidationError(null);
    setValidationStatus(nextValue.trim() ? 'pending' : 'idle');
  };

  // Show whenever rules need blur validation and the field is not focused (suggestion pick, paste, tab away, etc.)
  const showValidateHint =
    validationStatus === 'pending' && Boolean(editedRules.trim()) && !rulesFieldFocused;

  const runValidation = useCallback(async () => {
    const trimmed = editedRules.trim();
    if (!trimmed || (!channelId && !emailId)) return;
    const requestId = ++validationRequestIdRef.current;
    setValidationStatus('validating');
    setValidationError(null);
    try {
      const result = await validateChannelBusinessRules(channelId, trimmed, {
        emailId: emailId || undefined,
      });
      if (requestId !== validationRequestIdRef.current) return;
      setValidationResult(result);
      setValidationStatus(result.status);
    } catch (err: unknown) {
      if (requestId !== validationRequestIdRef.current) return;
      const message = err instanceof Error ? err.message : 'Validation failed. Please try again.';
      setValidationResult(null);
      setValidationStatus('pending');
      setValidationError(message);
    }
  }, [channelId, editedRules, emailId]);

  useEffect(() => {
    const trimmedInitial = initialBusinessRules?.trim() || '';
    const trimmedEdited = editedRules?.trim() || '';
    if (!trimmedInitial || trimmedEdited !== trimmedInitial) return;
    if (!channelId && !emailId) return;
    if (hasAutoValidatedOnMountRef.current) return;

    hasAutoValidatedOnMountRef.current = true;
    void runValidation();
  }, [initialBusinessRules, editedRules, channelId, emailId, runValidation]);

  if (variant === 'display') {
    if (!displayRules) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Business Rules
        </Typography>
        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
          {displayRules}
        </Typography>
      </Box>
    );
  }

  const getValidationIssue = (
    issue: string | BusinessRulesValidationIssue,
  ): BusinessRulesValidationIssue => {
    if (typeof issue === 'string') {
      return { message: issue };
    }
    return issue;
  };

  const getValidationIssueMessage = (
    issue: string | BusinessRulesValidationIssue,
  ): string => {
    const normalized = getValidationIssue(issue);
    if (normalized.message?.trim()) return normalized.message;
    if (normalized.check?.trim()) return normalized.check;
    return 'Validation issue found.';
  };

  const rulesHelperTextBase = isMessagingChannel
    ? "Describe what should happen when messages arrive — in plain English. For example: 'For urgent customer issues, find a resolution from the knowledge base and notify the support team.'"
    : "Describe what should happen when emails arrive — in plain English. For example: 'For urgent technical issues, find a resolution from the knowledge base and notify the support team.'";
  const rulesHelperText = showCharacterCount
    ? `${rulesHelperTextBase} (${editedRules.length}/${maxLength})`
    : rulesHelperTextBase;

  const rulesExpandDialog = showRulesExpand && (
    <Dialog
      open={isRulesExpanded}
      onClose={() => setIsRulesExpanded(false)}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          color: 'text.primary',
        },
      }}
      sx={{ zIndex: 9999 }}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        <Typography variant="h6" component="span">
          Business Rules
        </Typography>
        <IconButton
          onClick={() => setIsRulesExpanded(false)}
          size="small"
          aria-label="Close expanded business rules"
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={(theme) => ({
          bgcolor: 'background.paper',
          // MUI sets only 8px top padding after DialogTitle — override so the outlined label is not clipped
          paddingTop: `${theme.spacing(3)} !important`,
        })}
      >
        <TextField
          fullWidth
          margin="normal"
          autoFocus
          label="Rules"
          value={editedRules}
          onChange={(e) => handleRulesInputChange(e.target.value)}
          onFocus={() => setRulesFieldFocused(true)}
          onBlur={() => {
            setRulesFieldFocused(false);
            if (validationStatus === 'pending') {
              void runValidation();
            }
          }}
          multiline
          minRows={14}
          disabled={!isChannelAdmin}
          InputLabelProps={{
            required: false,
            shrink: true,
            sx: {
              bgcolor: 'background.paper',
              px: 0.5,
            },
          }}
          helperText={rulesHelperText}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, bgcolor: 'background.paper' }}>
        <Button onClick={() => setIsRulesExpanded(false)} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderRulesTextField = (options?: {
    placeholder?: string;
    minRows?: number;
    maxRows?: number;
    sx?: object;
  }) => (
    <Box sx={{ position: 'relative', ...(options?.sx ?? {}) }}>
      <TextField
        fullWidth
        label="Rules"
        value={editedRules}
        onChange={(e) => handleRulesInputChange(e.target.value)}
        onFocus={() => setRulesFieldFocused(true)}
        onBlur={() => {
          setRulesFieldFocused(false);
          if (validationStatus === 'pending') {
            void runValidation();
          }
        }}
        placeholder={options?.placeholder}
        multiline
        minRows={options?.minRows ?? 4}
        maxRows={options?.maxRows ?? 10}
        disabled={!isChannelAdmin}
        inputProps={{ maxLength }}
        inputRef={showRulesExpand ? rulesTextareaRef : undefined}
        InputLabelProps={{
          required: false,
        }}
        sx={
          {
            ...(showRulesExpand
              ? {
                '& .MuiInputBase-inputMultiline': {
                  pr: 4.5,
                },
              }
              : {}),
            ...(validationStatus === 'errors' && {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-border-danger, #d32f2f)',
              },
            }),
            ...(validationStatus === 'warnings' && {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-border-warning, #ed6c02)',
              },
            }),
            ...(validationStatus === 'clean' && {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-border-success, #2e7d32)',
              },
            }),
          }
        }
      />
      {showValidateHint && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mt: 1,
            p: 1.5,
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 1,
            backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main', mt: 0.25, flexShrink: 0 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flex: 1, minWidth: 0, width: '100%', lineHeight: 1.5 }}
          >
            Click Inside the Rules field to run validation.
          </Typography>
        </Box>
      )}
      {validationStatus === 'validating' && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <AutorenewIcon sx={{ fontSize: 16, animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
          <Typography variant="caption">Validating business rules...</Typography>
        </Box>
      )}
      {validationError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {validationError}
        </Alert>
      )}
      {validationStatus !== 'validating' && validationResult && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            // Warnings switched from the warning-orange palette to an info-blue one alongside
            // the icon/wording change above — recommendations aren't blocking, so the whole
            // box now reads as advisory rather than as a warning, not just the glyph.
            backgroundColor:
              validationResult.status === 'errors'
                ? 'var(--color-background-danger, rgba(211,47,47,0.08))'
                : validationResult.status === 'warnings'
                  ? 'var(--color-background-info, rgba(2,136,209,0.08))'
                  : 'var(--color-background-success, rgba(46,125,50,0.08))',
            borderColor:
              validationResult.status === 'errors'
                ? 'var(--color-border-danger, #d32f2f)'
                : validationResult.status === 'warnings'
                  ? 'var(--color-border-info, #0288d1)'
                  : 'var(--color-border-success, #2e7d32)',
          }}
        >
          {/* Fix 14/Item 7 — warnings get a fully separate, self-contained fragment (header +
              body) rather than sharing the errors/clean header below: no count anywhere, and the
              body is a generic sentence plus one verbatim tip line per app-connection warning
              (never a canned sentence naming apps that weren't actually flagged), instead of an
              itemized list. */}
          {validationResult.status === 'warnings' ? (() => {
            const { appWarnings, otherWarnings } = partitionWarnings(validationResult.warnings || []);
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: 'var(--color-text-info, #0288d1)' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Recommendations for this workflow
                  </Typography>
                </Box>
                {otherWarnings.length > 0 && (
                  <Typography variant="body2">
                    Some steps could use more detail, like a recipient email — open the workflow
                    canvas to review and fill them in.
                  </Typography>
                )}
                {appWarnings.map((w, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                    <LightbulbOutlinedIcon sx={{ fontSize: 16, mt: 0.25, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Tip: {getValidationIssueMessage(w)}
                    </Typography>
                  </Box>
                ))}
              </>
            );
          })() : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: validationResult.status === 'clean' ? 0 : 1 }}>
                {validationResult.status === 'errors' ? (
                  <ErrorOutlineIcon sx={{ fontSize: 18, color: 'var(--color-text-danger, #d32f2f)' }} />
                ) : (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'var(--color-text-success, #2e7d32)' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {validationResult.status === 'errors'
                    ? `${validationResult.errors?.length || 0} error(s) — fix before saving`
                    : 'Rules validated — all checks passed'}
                </Typography>
              </Box>
              {validationResult.status === 'errors' && (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {(validationResult.errors || []).map((item, idx) => {
                    const issue = getValidationIssue(item);
                    return (
                    <Typography
                      key={`${getValidationIssueMessage(item)}-${idx}`}
                      component="li"
                      variant="body2"
                      sx={{ mb: 0.5 }}
                    >
                      {getValidationIssueMessage(item)}
                      {issue.cta_url && issue.cta_label && (
                          <>
                            {' '}
                            <Link href={issue.cta_url} underline="hover">
                              {issue.cta_label}
                            </Link>
                          </>
                        )}
                    </Typography>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {rulesHelperText}
      </Typography>
      {showRulesExpand && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: rulesHasVerticalScroll && rulesScrollbarWidth > 0
              ? RULES_EXPAND_ICON_BASE_RIGHT + rulesScrollbarWidth + RULES_EXPAND_ICON_SCROLL_GAP
              : RULES_EXPAND_ICON_BASE_RIGHT,
            zIndex: 1,
          }}
        >
          <Tooltip title="Expand">
            <IconButton
              onClick={() => setIsRulesExpanded(true)}
              size="small"
              aria-label="Expand business rules"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {/* Configure email (suggestTrigger="auto"): expand icon intentionally hidden above */}
    </Box>
  );

  // Moved above the early-return branch below (and above businessRuleActionButtons, which
  // references it) — previously defined further down, after that branch's `if`, which is fine
  // for a plain function declaration but not for this `const`: referencing it from
  // businessRuleActionButtons before its own declaration line would throw
  // "Cannot access 'handleSave' before initialization" on every render.
  const handleSave = async () => {
    if (
      !editedRules.trim() ||
      !channelId ||
      validationStatus === 'pending' ||
      validationStatus === 'errors' ||
      validationStatus === 'validating' ||
      (validationStatus === 'idle' && editedRules.trim() !== '')
    ) {
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const trimmed = editedRules.trim();
      await saveChannelBusinessRules(channelId, trimmed, {
        forceSave: validationStatus === 'warnings',
        validationResult,
      });
      setSaveSuccess(true);
      onSaved?.(trimmed);
      showSuccess('Business rules saved. Automation is being configured...');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save business rules';
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Business_rule_canvas branch (TDD §8.2) — Save/"See workflow canvas" actions, shared between
  // this component's two return paths below. Previously only the main return (further down)
  // had this block, which meant "See workflow canvas" silently never rendered for the single
  // most common real case: an existing channel with saved rules opened via "Edit Channel"
  // (suggestTrigger="open" flips showSavedRulesView true whenever initialBusinessRules is
  // non-empty — see the mount-time useState and the "saved rules arrive after mount" effect —
  // which routes to the early-return branch just below instead of the main one). Defined once so
  // the two paths can't silently diverge like this again.
  const businessRuleActionButtons = isChannelAdmin && (saveMode === 'immediate' || onBuildWorkflow) && (
    <Box ref={workflowActionButtonsRef} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {saveMode === 'immediate' && (
        <Button
          variant="contained"
          size="small"
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={
            isSaving ||
            !editedRules.trim() ||
            validationStatus === 'pending' ||
            validationStatus === 'validating' ||
            validationStatus === 'errors' ||
            (validationStatus === 'idle' && editedRules.trim() !== '')
          }
          sx={{ mt: 2 }}
        >
          {isSaving ? 'Saving...' : validationStatus === 'warnings' ? 'Save rules (with known gaps)' : 'Save rules'}
        </Button>
      )}
      {onBuildWorkflow && (
        // Same enabled/disabled gate as Save above (rules non-empty AND validationStatus is
        // 'clean' or 'warnings'). ml: 'auto' pushes just this button to the right edge of the
        // row (rather than justifyContent on the wrapping Box), so "Save rules" — when it's also
        // present — stays left-aligned exactly as before; only this button's own position moves.
        <Button
          variant="outlined"
          size="small"
          startIcon={<WorkflowCanvasIcon />}
          onClick={() => onBuildWorkflow(editedRules)}
          disabled={
            !editedRules.trim() ||
            !(validationStatus === 'clean' || validationStatus === 'warnings')
          }
          sx={{ mt: 2, ml: 'auto' }}
        >
          See workflow canvas
        </Button>
      )}
    </Box>
  );

  // "Improve my rules" (TDD §6.4) — edit dialog only, saved rules only, never auto-run, and only
  // when VITE_ENABLE_IMPROVE_RULES is on (switched on together with the backend flags). The server
  // refines the channel's SAVED rules (looked up by channel_id), so unsaved textarea edits are
  // not part of the request.
  const canImproveRules =
    isImproveRulesEnabled() &&
    suggestTrigger === 'open' &&
    isChannelAdmin &&
    Boolean(channelId.trim()) &&
    hasExistingRules &&
    canSuggest &&
    !refineUnsupported;

  const handleImproveRules = () => {
    const trimmedDescription = channelDescription?.trim() || '';
    if (!canImproveRules || !trimmedDescription || isSuggesting) return;
    void fetchSuggestions(trimmedDescription, { clearRules: false, refine: true });
  };

  // Edit channel dialog: saved rules shown like Description field (no suggestion cards).
  // "View suggestions" is still offered here as a peek — clicking it fetches suggestion
  // cards (clearRules: false, so the saved rules text is left untouched) and leaves this
  // saved-rules view for the normal suggestions view below. It never auto-fires here; per
  // the product decision above, only an explicit click (or picking a new description) may
  // trigger a refresh of an existing channel's saved rules.
  if (suggestTrigger === 'open' && showSavedRulesView) {
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Business Rules{' '}
            {required && (
              <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
            )}
          </Typography>
          {(canImproveRules || canSuggest) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {canImproveRules && (
                <Tooltip title="Suggests an improved version of your saved rules. Nothing changes until you pick one.">
                  <span>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleImproveRules}
                      disabled={isSuggesting}
                      sx={{ textTransform: 'none', minWidth: 'auto' }}
                    >
                      Improve my rules
                    </Button>
                  </span>
                </Tooltip>
              )}
              {canSuggest && (
                <Button
                  variant="text"
                  size="small"
                  disabled={isSuggesting}
                  onClick={() => {
                    setShowSavedRulesView(false);
                    // PR #672 review (M2) — a click is a deliberate refresh: without this, the
                    // dedupe key from a previous fetch for the same description can still match
                    // (e.g. right after save re-enters this saved view and clears suggestions but
                    // not the key), silently no-opping the fetch and leaving the admin in the
                    // non-saved view with no cards, no loader, and no error.
                    lastSuggestKeyRef.current = '';
                    fetchSuggestions(channelDescription?.trim() || '', { clearRules: false });
                  }}
                  sx={{ textTransform: 'none', minWidth: 'auto' }}
                >
                  {isSuggesting ? 'Loading suggestions...' : 'View suggestions'}
                </Button>
              )}
            </Box>
          )}
        </Box>
        {refineNotice && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRefineNotice(null)}>
            {refineNotice}
          </Alert>
        )}
        {/* PR #672 re-review (M3) — this alert previously only rendered in the main return
            below (after this saved-view branch's own early return), so when the peek's fetch
            failed or returned nothing, the failure-return effect flipped back to this view with
            no explanation at all: the admin just saw a spinner then silence. Same alert, same
            dismiss behavior, now visible here too. */}
        {suggestError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSuggestError(null)}>
            {suggestError}
          </Alert>
        )}
        {renderRulesTextField()}
        {rulesExpandDialog}
        {businessRuleActionButtons}
      </Box>
    );
  }

  const applySuggestion = (rule: string) => {
    setEditedRules(rule);
    setSaveSuccess(false);
    setShowRulesField(true);
    setShowRulesCards(false);
    setRefineActive(false);
    setPendingReplacement(null);
    setValidationResult(null);
    setValidationError(null);
    setValidationStatus(rule.trim() ? 'pending' : 'idle');
  };

  // A refined option overwrites text the admin already has, so picking one needs an explicit
  // confirm (TDD §6.4). Fresh-generation cards keep the existing one-click behaviour.
  const handleSelectSuggestion = (rule: string) => {
    const current = editedRules.trim();
    if (refineActive && current && current !== rule.trim()) {
      setPendingReplacement(rule);
      return;
    }
    applySuggestion(rule);
  };

  const handleToggleRulesView = () => {
    if (showRulesCards) {
      setShowRulesCards(false);
      setShowRulesField(true);
    } else {
      setShowRulesCards(true);
      setShowRulesField(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Business Rules{' '}
            {required && (
              <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isMessagingChannel
              ? 'Tell Prism7 what to do when messages arrive in this channel.'
              : 'Tell Prism7 what to do when emails arrive in this channel.'}
          </Typography>
        </Box>
        {saveMode === 'immediate' && saveSuccess && (
          <Chip
            icon={<CheckIcon />}
            label="Saved"
            color="success"
            size="small"
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>

      {!hasDescription && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Set a channel description first — it helps generate better automation rules.
        </Alert>
      )}

      {hasDescription && !hasWorkspace && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Set a workspace for this channel — it helps generate better automation rules.
        </Alert>
      )}

      {!isMessagingChannel && hasDescription && hasWorkspace && !hasEmail && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Connect an email to this channel — it helps generate better automation rules.
        </Alert>
      )}

      {/* Manual suggest button removed — API auto-fetches when description is selected
      {isChannelAdmin && (
        <Button
          variant="outlined"
          size="small"
          startIcon={isSuggesting ? <CircularProgress size={16} /> : undefined}
          onClick={handleSuggest}
          disabled={isSuggesting || !canSuggest}
          sx={{ mb: 2, textTransform: 'uppercase' }}
        >
          {isSuggesting ? 'Generating suggestions...' : 'Suggest automation rules'}
        </Button>
      )}
      */}

      {suggestError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSuggestError(null)}>
          {suggestError}
        </Alert>
      )}

      {(showRulesCards || isSuggesting) && !showSavedRulesView && (
        <Box
          sx={{
            mb: 2,
            minHeight: isSuggesting && suggestions.length === 0 ? '250px' : 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                {refineActive ? 'Select an improved version of your rules' : 'Select a Business rule for your channel'}
              </Typography>
              {(refineActive || suggestionsMode) && (
                <Typography variant="caption" color="text.secondary">
                  {refineActive
                    ? 'New sentences are shown in bold. Your current rules stay unchanged until you confirm.'
                    : 'Suggested rules — assign people on the canvas.'}
                </Typography>
              )}
            </Box>
            {suggestions.length > 0 && editedRules.trim() !== '' && (
              <Button
                variant="text"
                size="small"
                onClick={handleToggleRulesView}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                {showRulesCards ? 'Edit manually' : 'View suggestions'}
              </Button>
            )}
          </Box>
          {(() => {
            // Edit Channel "View suggestions" peek: the regenerated suggestions are fresh
            // text and will (almost) never match the already-saved rules verbatim, so none
            // of them would show as "Selected" and the admin loses track of what's currently
            // active. Prepend the ORIGINAL saved rules (serverRules) as its own card so it
            // still shows selected, with the new suggestions as alternatives below. Does not
            // affect the 'auto' (create-channel) flow — hasExistingRules is always false
            // there, since a new channel has no saved rules yet.
            //
            // PR #672 review (M1) — this card must be built from `serverRules` (the saved
            // value), not `editedRules` (the live text). Using editedRules made the card
            // follow every keystroke and disappear the moment the admin picked a suggestion
            // (handleSelectSuggestion sets editedRules to the pick), losing the one card that
            // let them switch back to the original rules — exactly the case it exists for.
            // `selectedDescription={editedRules}` below is unchanged and still correctly
            // highlights whichever card (original or a suggestion) matches the live value.
            const showCurrentAsCard =
              suggestTrigger === 'open' &&
              hasExistingRules &&
              serverRules !== '' &&
              !suggestions.some((s) => s.trim() === serverRules);
            const cardDescriptions = showCurrentAsCard
              ? [serverRules, ...suggestions]
              : suggestions;
            return (
              <DescriptionCards
                descriptions={cardDescriptions}
                loading={isSuggesting}
                selectedDescription={editedRules}
                onSelect={handleSelectSuggestion}
                onEdit={handleSelectSuggestion}
                disabled={!isChannelAdmin}
                loadingMessage="Fetching Business rule suggestions for your channel"
                updatingMessage="Updating Business rule suggestions..."
                emptyMessage="No Business rule suggestions available."
                suggestionLabel="Rule"
                firstCardLabel={showCurrentAsCard ? 'Current rules' : undefined}
                // "Improve my rules": the backend refines the SAVED rules, so "new" means not in
                // serverRules (not the live textarea, which may hold unsaved edits).
                highlightBaseline={refineActive ? serverRules : undefined}
              />
            );
          })()}
        </Box>
      )}

      {(showRulesField || showSavedRulesView) && (
        <>
          {suggestions.length > 0 && !showRulesCards && !showSavedRulesView && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={handleToggleRulesView}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                View suggestions
              </Button>
            </Box>
          )}
          {renderRulesTextField({
            placeholder: canSuggest
              ? 'Write or paste your automation rules here, or select a suggested rule above...'
              : 'Set a channel description first, then write your automation rules here.',
            sx: { mb: saveMode === 'immediate' ? 2 : 0 },
          })}
        </>
      )}

      {rulesExpandDialog}

      {/* Same structure as the other confirm popups (e.g. Remove Member in MembersTab): plain
          Dialog, plain title, message in a padded Box, Cancel + one contained action. */}
      <Dialog
        open={pendingReplacement !== null}
        onClose={() => setPendingReplacement(null)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle>Replace your current rules?</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              This replaces your current text; you can still edit it.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingReplacement(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingReplacement !== null) applySuggestion(pendingReplacement);
            }}
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>

      {businessRuleActionButtons}
    </Box>
  );
};

export default BusinessRulesSection;
